import type { ApiError } from "@circle-react/config/CustomErrors";
import {
  flaggedContentInvalidations,
  flaggedContentQueries,
} from "@circle-react/lib/queries/flaggedContent";
import { postInvalidations } from "@circle-react/lib/queries/posts";
import { useMutation, useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import { POST_TYPE } from "@/react/types/FlaggedContent";

import type { ModerationActionOptions } from "../useModerationApi";
import { useModerationApi } from "../useModerationApi";

const i18nRoot = "settings.moderation.review_flagged_content_modal";

interface ApproveParams {
  options?: ModerationActionOptions;
}

interface RejectParams {
  options?: ModerationActionOptions;
  reason: string | null;
}

export const useApproveRejectContent = (flaggedContentId: string) => {
  const { success, error } = useToast();
  const { approve, reject } = useModerationApi();

  const { data: flaggedContent, isInitialLoading } = useQuery({
    ...flaggedContentQueries.detail(flaggedContentId),
    enabled: !!flaggedContentId,
  });

  const invalidateTableQueries = () => {
    void flaggedContentInvalidations.all();
  };

  const invalidateContentQueries = () => {
    invalidateTableQueries();

    if (flaggedContent?.flagged_contentable?.type !== POST_TYPE) return;

    const postId = flaggedContent.flagged_contentable.id;
    if (!postId) return;

    void postInvalidations.allPosts();
    void postInvalidations.postDetails(postId);
  };

  const { mutate: approveFlaggedContent, isPending: isApproving } = useMutation<
    unknown,
    ApiError,
    ApproveParams
  >({
    mutationFn: (params: ApproveParams) =>
      approve(flaggedContentId, params.options),
    onSuccess: () => {
      invalidateContentQueries();
      success(t([i18nRoot, "success_approving"]));
    },
    onError: () => error(t([i18nRoot, "error_approving"])),
  });

  const { mutate: rejectFlaggedContent, isPending: isRejecting } = useMutation<
    unknown,
    ApiError,
    RejectParams
  >({
    mutationFn: (params: RejectParams) =>
      reject(flaggedContentId, params.reason, params.options),
    onSuccess: () => {
      invalidateContentQueries();
      success(t([i18nRoot, "success_rejecting"]));
    },
    onError: () => error(t([i18nRoot, "error_rejecting"])),
  });

  return {
    approveFlaggedContent,
    isApproving,
    rejectFlaggedContent,
    isRejecting,
    isLoading: isInitialLoading,
    flaggedContent,
  };
};
